import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Card,
  CardActions,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { FunctionComponent } from 'react';

import { Dialog } from '../../components/Dialog';
import { Layout } from '../../navigation/components/Layout/Layout';
import { PaymentDialog } from '../AdminEventUsers/components/PaymentDialog/PaymentDialog';
import { CheckboxFilterType } from '../AdminEventUsers/useAdminEventUsers';
import { ScanQrCodeModal } from './components/ScanQrCodeModal/ScanQrCodeModal';
import { UserRecapModal } from './components/UserRecapModal/UserRecapModal';
import { useAdminValidation } from './useAdminValidation';

const cautionLabel: Record<CheckboxFilterType, string> = {
  all: 'Tous',
  only: 'Avec caution',
  'only-not': 'Sans caution',
};

const paiementLabel: Record<CheckboxFilterType, string> = {
  all: 'Tous',
  only: 'Payé uniquement',
  'only-not': 'Pas payé uniquement',
};

const validationLabel: Record<CheckboxFilterType, string> = {
  all: 'Tous',
  only: 'Validé',
  'only-not': 'Pas validé',
};

const isHereLabel: Record<CheckboxFilterType, string> = {
  all: 'Tous',
  only: 'Présent',
  'only-not': 'Absent',
};

export const AdminValidation: FunctionComponent = () => {
  const {
    dialog,
    eventUsers,
    loading,
    clearCode,
    code,
    onCodeChange,
    onSubmit,
    onHerePress,
    onCautionPress,
    onCloseDialog,
    onPaymentPress,
    onValidatePress,
    onClosePaymentDialog,
    paymentDialogInformation,
    onRecapModalClose,
    isRecapModalOpen,
    lastConfirmedUser,
    onOpenQrCodeScanner,
    isQrCodeModalOpen,
    onCloseQrCodeScanner,
    onQrCodeSan,
    getArrivalDayNameByDay,
    onPaiementFilterCheckboxChange,
    paiementFilter,
    cautionFilter,
    onCautionFilterCheckboxChange,
    onValidationFilterCheckboxChange,
    validationFilter,
    clearSearch,
    onSearchChange,
    search,
    isHereFilter,
    onIsHereFilterCheckboxChange,
    arrivalDayOptions,
    arrivalDayFilter,
    onArrivalDayFilterChange,
  } = useAdminValidation();

  const columns: GridColDef[] = [
    { field: 'firstname', headerName: 'Prénom' },
    { field: 'lastname', headerName: 'Nom' },
    { field: 'studyTown', headerName: 'Ville' },
    { field: 'nickname', headerName: 'Surnom' },
    {
      field: 'hasPaidTicket',
      headerName: 'Payé',
      align: 'center',
      headerAlign: 'center',
      width: 70,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.hasPaidTicket}
          onClick={() => onPaymentPress(params.row)}
        />
      ),
    },
    {
      field: 'isValidated',
      headerName: 'Validé',
      align: 'center',
      headerAlign: 'center',
      width: 70,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.isValidated}
          onClick={() => onValidatePress(params.row)}
        />
      ),
    },
    {
      field: 'hasCaution',
      headerName: 'Caution',
      align: 'center',
      headerAlign: 'center',
      width: 70,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.hasCaution}
          onClick={() => onCautionPress(params.row)}
        />
      ),
    },
    {
      field: 'isHere',
      headerName: 'Présent',
      align: 'center',
      headerAlign: 'center',
      width: 70,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.isHere}
          onClick={() => onHerePress(params.row)}
        />
      ),
    },
    {
      field: 'arrivalDay',
      headerName: "Jour d'arrivée",
      align: 'center',
      headerAlign: 'center',
      width: 110,
      renderCell: (params) => getArrivalDayNameByDay(params.row.arrivalDay),
    },
    {
      field: 'numberOfDays',
      headerName: 'Nombre de jours',
      align: 'center',
      headerAlign: 'center',
      width: 130,
    },
  ];

  return (
    <Layout title="Validation des entrée" activeTab="validation">
      <Card sx={{ mb: 2 }}>
        <CardActions>
          <Typography>Filtres:</Typography>
        </CardActions>
        <CardActions>
          <FormControlLabel
            label={`Caution (${cautionLabel[cautionFilter]})`}
            control={
              <Checkbox
                checked={cautionFilter === 'only'}
                indeterminate={cautionFilter === 'all'}
                onChange={onCautionFilterCheckboxChange}
              />
            }
          />

          <FormControlLabel
            label={`Paiement (${paiementLabel[paiementFilter]})`}
            control={
              <Checkbox
                checked={paiementFilter === 'only'}
                indeterminate={paiementFilter === 'all'}
                onChange={onPaiementFilterCheckboxChange}
              />
            }
          />

          <FormControlLabel
            label={`Validé (${validationLabel[validationFilter]})`}
            control={
              <Checkbox
                checked={validationFilter === 'only'}
                indeterminate={validationFilter === 'all'}
                onChange={onValidationFilterCheckboxChange}
              />
            }
          />

          <FormControlLabel
            label={`Présent (${isHereLabel[isHereFilter]})`}
            control={
              <Checkbox
                checked={isHereFilter === 'only'}
                indeterminate={isHereFilter === 'all'}
                onChange={onIsHereFilterCheckboxChange}
              />
            }
          />

          <Select
            value={arrivalDayFilter}
            onChange={onArrivalDayFilterChange}
            placeholder="Jour d'arrivé"
          >
            <MenuItem value="all">Tous les jours</MenuItem>
            {arrivalDayOptions.map((arrivalDayOption) => (
              <MenuItem
                key={arrivalDayOption.value}
                value={arrivalDayOption.value}
              >
                {arrivalDayOption.label}
              </MenuItem>
            ))}
          </Select>
        </CardActions>
      </Card>
      <Card sx={{ mb: 2 }}>
        <CardActions>
          <TextField
            label="Rechercher un participant"
            size="small"
            value={search}
            onChange={onSearchChange}
            InputProps={{
              startAdornment: <SearchIcon />,
              endAdornment:
                search.length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearch} onMouseDown={clearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
            }}
          />
        </CardActions>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardActions>
          <TextField
            label="Valider un code"
            placeholder="XXXXXX"
            size="medium"
            value={code}
            onChange={onCodeChange}
            onSubmit={onSubmit}
            InputProps={{
              startAdornment: <SearchIcon />,
              endAdornment:
                code.length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton onClick={clearCode} onMouseDown={clearCode}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
            }}
          />
          <Button onClick={onSubmit}>Valider</Button>
          <Button onClick={onOpenQrCodeScanner}>SCAN</Button>
        </CardActions>
      </Card>

      <DataGrid
        rows={eventUsers}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        loading={loading}
      />
      {dialog ? (
        <Dialog
          title={dialog.title}
          description={dialog.description}
          onSubmit={dialog.onSubmit}
          onCancel={onCloseDialog}
          open
        />
      ) : null}
      {paymentDialogInformation ? (
        <PaymentDialog
          open
          onClose={onClosePaymentDialog}
          userId={paymentDialogInformation.userId}
          eventId={paymentDialogInformation.eventId}
        />
      ) : null}
      <UserRecapModal
        isOpen={isRecapModalOpen}
        onClose={onRecapModalClose}
        user={lastConfirmedUser}
      />
      {isQrCodeModalOpen ? (
        <ScanQrCodeModal
          isOpen={isQrCodeModalOpen}
          onClose={onCloseQrCodeScanner}
          onScan={onQrCodeSan}
        />
      ) : null}
    </Layout>
  );
};
